import React from 'react';

export interface FinishedGoodsReportItem {
  id: string;
  finalQcId: string;
  checkTime: Date | string;
  poId: string;
  poCode: string;
  customerId: string;
  customerName: string;
  productCode: string;
  productName: string;
  unit: string;
  qtyPacked: number;
  unitPrice: number;
  totalAmount: number;
  cumulativeQty: number;
  qtyOrder: number;
  qtyPlan: number;
  remainingQty: number;
  remainingAmount: number;
  inspectorName: string;
  notes: string;
  boxDetails?: {
    pcsPerBox?: number;
    totalBoxes?: number;
    totalPallets?: number;
    lotNumber?: string;
    receiptDocNo?: string;
  };
}

export interface FinishedGoodsQCReportPrintContentProps {
  reportData: {
    summary: {
      totalPackedQty: number;
      totalPackedAmount: number;
      averageCompletionRate: number;
      totalRemainingQty: number;
      totalRemainingAmount: number;
    };
    items: FinishedGoodsReportItem[];
    canSeePrice?: boolean;
  };
  dateRange?: { from?: string; to?: string };
  config?: any;
  orientation?: 'portrait' | 'landscape';
  groupBy?: 'date' | 'customer' | 'none';
}

export default function FinishedGoodsQCReportPrintContent({
  reportData,
  dateRange,
  config,
  orientation = 'portrait',
  groupBy = 'date',
}: FinishedGoodsQCReportPrintContentProps) {
  const { summary, items = [], canSeePrice = false } = reportData || { summary: {}, items: [] };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatNumber = (val?: number | string) => {
    if (val === null || val === undefined || val === '') return '0';
    if (isNaN(Number(val))) return String(val);
    return Number(val).toLocaleString('vi-VN');
  };

  const isLandscape = orientation === 'landscape';

  // Group items if requested
  const groups: { [key: string]: FinishedGoodsReportItem[] } = {};
  if (groupBy === 'date') {
    items.forEach(item => {
      const key = formatDate(item.checkTime);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
  } else if (groupBy === 'customer') {
    items.forEach(item => {
      const key = item.customerName || 'Khách lẻ';
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
  } else {
    groups['all'] = items;
  }

  const groupKeys = Object.keys(groups);

  return (
    <div
      id="print-area"
      className={`kh-print-doc w-full bg-white text-black p-[8mm] font-serif mx-auto text-[9pt] leading-tight print:p-0 print:m-0 ${
        isLandscape ? 'max-w-[280mm]' : 'max-w-[190mm]'
      }`}
      style={{ fontFamily: '"Times New Roman", Times, serif' }}
    >
      {/* ========== HEADER COMPANY & ISO BADGE ========== */}
      <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-3">
        <div className="flex-shrink-0 flex items-center gap-2 w-[100px]">
          {config?.logoUrl ? (
            <img
              src={config.logoUrl}
              alt="Logo"
              className="h-[50px] max-w-[100px] object-contain"
            />
          ) : (
            <div className="h-[50px] w-[90px] flex items-center justify-center border border-slate-400 bg-slate-50 text-slate-700 font-bold text-[11px]">
              APV PACK
            </div>
          )}
        </div>

        <div className="text-center flex-1 px-3">
          <div className="text-[19px] font-bold uppercase tracking-wide text-slate-900">
            BÁO CÁO THÀNH PHẨM &amp; KIỂM ĐỊNH KCS
          </div>
          <div className="text-[10pt] text-slate-700 mt-1 font-semibold">
            <span>Thời gian: </span>
            <strong>{dateRange?.from ? formatDate(dateRange.from) : 'Từ trước'}</strong>
            <span> — </span>
            <strong>{dateRange?.to ? formatDate(dateRange.to) : 'Đến nay'}</strong>
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-col items-end justify-center w-[100px] text-[8pt] text-slate-600 text-right">
          <div className="border border-slate-300 rounded px-1.5 py-1 bg-slate-50">
            <div className="font-semibold text-slate-800">BM-KCS-01</div>
            <div>Ban hành: 01/2025</div>
          </div>
        </div>
      </div>

      {/* ========== KPI SUMMARY CARDS ========== */}
      <div className="grid grid-cols-5 gap-2 mb-3 print:grid-cols-5 print:gap-1">
        <div className="p-2 rounded bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 text-center print:bg-white print:border-black print:shadow-none">
          <div className="text-[8pt] text-indigo-900 font-semibold uppercase">Tổng SL Đóng Gói</div>
          <div className="text-[13pt] font-bold text-indigo-950 mt-0.5">{formatNumber(summary.totalPackedQty)}</div>
        </div>
        {canSeePrice ? (
          <div className="p-2 rounded bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 text-center print:bg-white print:border-black print:shadow-none">
            <div className="text-[8pt] text-emerald-900 font-semibold uppercase">Doanh Số Thành Phẩm</div>
            <div className="text-[13pt] font-bold text-emerald-950 mt-0.5">{formatNumber(summary.totalPackedAmount)} đ</div>
          </div>
        ) : (
          <div className="p-2 rounded bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 text-center print:bg-white print:border-black print:shadow-none">
            <div className="text-[8pt] text-emerald-900 font-semibold uppercase">Số Lô Kiểm Định</div>
            <div className="text-[13pt] font-bold text-emerald-950 mt-0.5">{items.length} lô</div>
          </div>
        )}
        <div className="p-2 rounded bg-gradient-to-br from-sky-50 to-sky-100 border border-sky-200 text-center print:bg-white print:border-black print:shadow-none">
          <div className="text-[8pt] text-sky-900 font-semibold uppercase">Tỷ Lệ Hoàn Thành</div>
          <div className="text-[13pt] font-bold text-sky-950 mt-0.5">{summary.averageCompletionRate || 0}%</div>
        </div>
        <div className="p-2 rounded bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 text-center print:bg-white print:border-black print:shadow-none">
          <div className="text-[8pt] text-amber-900 font-semibold uppercase">Tổng SL Còn Thiếu</div>
          <div className="text-[13pt] font-bold text-amber-950 mt-0.5">{formatNumber(summary.totalRemainingQty)}</div>
        </div>
        {canSeePrice ? (
          <div className="p-2 rounded bg-gradient-to-br from-rose-50 to-rose-100 border border-rose-200 text-center print:bg-white print:border-black print:shadow-none">
            <div className="text-[8pt] text-rose-900 font-semibold uppercase">Giá Trị Còn Thiếu</div>
            <div className="text-[13pt] font-bold text-rose-950 mt-0.5">{formatNumber(summary.totalRemainingAmount)} đ</div>
          </div>
        ) : (
          <div className="p-2 rounded bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 text-center print:bg-white print:border-black print:shadow-none">
            <div className="text-[8pt] text-purple-900 font-semibold uppercase">Tổng Số Thùng</div>
            <div className="text-[13pt] font-bold text-purple-950 mt-0.5">
              {formatNumber(items.reduce((sum, it) => sum + (it.boxDetails?.totalBoxes || 0), 0))}
            </div>
          </div>
        )}
      </div>

      {/* ========== REPORT DATA TABLE (13 COLUMNS) ========== */}
      <table className="w-full border-collapse border border-black text-[8.5pt]">
        <thead>
          <tr className="bg-slate-200 font-bold text-center border-b border-black">
            <th className="border border-black p-1 w-6">STT</th>
            <th className="border border-black p-1 w-16">Ngày</th>
            <th className="border border-black p-1 w-18">Lệnh SX</th>
            <th className="border border-black p-1 text-left w-24">Khách Hàng</th>
            <th className="border border-black p-1 text-left">Tên Sản Phẩm</th>
            <th className="border border-black p-1 w-10">ĐVT</th>
            <th className="border border-black p-1 w-16">SL Đóng Gói</th>
            {canSeePrice && <th className="border border-black p-1 w-16">Đơn Giá</th>}
            <th className="border border-black p-1 w-16">Lũy Kế SL</th>
            <th className="border border-black p-1 w-14">SL Đặt</th>
            <th className="border border-black p-1 w-14">SL KH</th>
            <th className="border border-black p-1 w-14">SL CL</th>
            {canSeePrice && <th className="border border-black p-1 w-20">TT CL</th>}
            <th className="border border-black p-1 text-left w-24">Ghi Chú</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={canSeePrice ? 14 : 12} className="border border-black p-4 text-center text-slate-500 italic">
                Không có dữ liệu thành phẩm &amp; KCS trong khoảng thời gian đã chọn.
              </td>
            </tr>
          ) : (
            groupKeys.map((gKey, gIdx) => {
              const groupItems = groups[gKey];
              const subtotalPacked = groupItems.reduce((sum, it) => sum + it.qtyPacked, 0);
              const subtotalRemaining = groupItems.reduce((sum, it) => sum + it.remainingQty, 0);
              const subtotalAmount = groupItems.reduce((sum, it) => sum + it.totalAmount, 0);

              return (
                <React.Fragment key={gKey}>
                  {groupBy !== 'none' && (
                    <tr className="bg-slate-100 font-bold text-slate-800">
                      <td colSpan={canSeePrice ? 14 : 12} className="border border-black p-1 text-left font-bold uppercase text-[8.5pt]">
                        📌 {groupBy === 'date' ? `Ngày kiểm: ${gKey}` : `Khách hàng: ${gKey}`} ({groupItems.length} bản ghi)
                      </td>
                    </tr>
                  )}

                  {groupItems.map((item, idx) => (
                    <tr key={item.id} className="hover:bg-slate-50 print:break-inside-avoid">
                      <td className="border border-black p-1 text-center font-medium">{idx + 1}</td>
                      <td className="border border-black p-1 text-center font-mono">{formatDate(item.checkTime)}</td>
                      <td className="border border-black p-1 text-center font-bold font-mono text-indigo-950">{item.poCode}</td>
                      <td className="border border-black p-1 font-semibold">{item.customerName}</td>
                      <td className="border border-black p-1 font-medium">{item.productName}</td>
                      <td className="border border-black p-1 text-center">{item.unit || 'Hộp'}</td>
                      <td className="border border-black p-1 text-center font-bold text-emerald-800">
                        {formatNumber(item.qtyPacked)}
                      </td>
                      {canSeePrice && (
                        <td className="border border-black p-1 text-right font-mono">
                          {item.unitPrice ? `${formatNumber(item.unitPrice)}` : '—'}
                        </td>
                      )}
                      <td className="border border-black p-1 text-center font-bold font-mono text-indigo-900">
                        {formatNumber(item.cumulativeQty)}
                      </td>
                      <td className="border border-black p-1 text-center font-medium">
                        {formatNumber(item.qtyOrder)}
                      </td>
                      <td className="border border-black p-1 text-center text-slate-700">
                        {formatNumber(item.qtyPlan)}
                      </td>
                      <td className="border border-black p-1 text-center font-bold text-amber-800">
                        {formatNumber(item.remainingQty)}
                      </td>
                      {canSeePrice && (
                        <td className="border border-black p-1 text-right font-mono font-bold text-rose-800">
                          {formatNumber(item.remainingAmount)}
                        </td>
                      )}
                      <td className="border border-black p-1 text-[8pt] text-slate-600">
                        {item.boxDetails?.totalBoxes ? `${item.boxDetails.totalBoxes} thùng (${item.boxDetails.pcsPerBox} sp/th) | ` : ''}
                        {item.notes || 'Đạt FQC'}
                      </td>
                    </tr>
                  ))}

                  {groupBy !== 'none' && (
                    <tr className="bg-emerald-50 font-bold border-b border-black text-[8.5pt]">
                      <td colSpan={6} className="border border-black p-1 text-right">
                        TỔNG PHẦN ({gKey}):
                      </td>
                      <td className="border border-black p-1 text-center text-emerald-950 font-bold">
                        {formatNumber(subtotalPacked)}
                      </td>
                      {canSeePrice && (
                        <td className="border border-black p-1 text-right text-emerald-950 font-bold">
                          {formatNumber(subtotalAmount)}
                        </td>
                      )}
                      <td className="border border-black p-1"></td>
                      <td className="border border-black p-1"></td>
                      <td className="border border-black p-1"></td>
                      <td className="border border-black p-1 text-center text-amber-950 font-bold">
                        {formatNumber(subtotalRemaining)}
                      </td>
                      {canSeePrice && <td className="border border-black p-1"></td>}
                      <td className="border border-black p-1"></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
        <tfoot>
          <tr className="bg-amber-100 font-bold text-black border-t-2 border-black text-[9pt]">
            <td colSpan={6} className="border border-black p-1.5 text-right font-extrabold uppercase">
              TỔNG CỘNG TOÀN KỲ:
            </td>
            <td className="border border-black p-1.5 text-center font-extrabold text-emerald-950 text-[9.5pt]">
              {formatNumber(summary.totalPackedQty)}
            </td>
            {canSeePrice && (
              <td className="border border-black p-1.5 text-right font-extrabold text-emerald-950">
                {formatNumber(summary.totalPackedAmount)} đ
              </td>
            )}
            <td className="border border-black p-1.5"></td>
            <td className="border border-black p-1.5"></td>
            <td className="border border-black p-1.5"></td>
            <td className="border border-black p-1.5 text-center font-extrabold text-amber-950 text-[9.5pt]">
              {formatNumber(summary.totalRemainingQty)}
            </td>
            {canSeePrice && (
              <td className="border border-black p-1.5 text-right font-extrabold text-rose-950">
                {formatNumber(summary.totalRemainingAmount)} đ
              </td>
            )}
            <td className="border border-black p-1.5"></td>
          </tr>
        </tfoot>
      </table>

      {/* ========== FOOTER SIGNATURES (4 BLOCKS) ========== */}
      <div className="mt-8 grid grid-cols-4 gap-4 text-center text-[9pt] print:break-inside-avoid">
        <div>
          <div className="font-bold mb-12">Người Lập Báo Cáo</div>
          <div className="font-semibold text-slate-800">Nhân Viên KCS</div>
        </div>
        <div>
          <div className="font-bold mb-12">Tổ Trưởng Đóng Gói</div>
          <div className="font-semibold text-slate-800">Tổ Đóng Gói</div>
        </div>
        <div>
          <div className="font-bold mb-12">Trưởng Phòng QA/QC</div>
          <div className="font-semibold text-slate-800">Trưởng Bộ Phận</div>
        </div>
        <div>
          <div className="font-bold mb-12">Quản Đốc / Ban Giám Đốc</div>
          <div className="font-semibold text-slate-800">Phê Duyệt</div>
        </div>
      </div>
    </div>
  );
}
